import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

export type FileUploadActivityContextValue = {
	// True while at least one plain (presigned S3) file transfer is in flight.
	isFileUploading: boolean;
	beginFileUpload: () => void;
	endFileUpload: () => void;
};

const FileUploadActivityContext = createContext<FileUploadActivityContextValue | null>(null);

/**
 * Tracks plain file uploads that are in flight. Unlike tileset uploads these
 * have no progress card of their own and their dialog can be dismissed
 * mid-transfer, so the flag lives here rather than in the dialog: every upload
 * entry point reads it (via useUploadBusy) to disable itself while a transfer
 * is running.
 */
export function FileUploadActivityProvider({ children }: { children: ReactNode }) {
	// A counter, not a boolean: several uploaders can be mounted at once (asset,
	// inspection and damage tabs each render one), so the flag must stay set
	// until the last transfer finishes.
	const [activeCount, setActiveCount] = useState(0);

	const beginFileUpload = useCallback(() => setActiveCount((count) => count + 1), []);
	const endFileUpload = useCallback(() => setActiveCount((count) => Math.max(0, count - 1)), []);

	return (
		<FileUploadActivityContext.Provider value={{ isFileUploading: activeCount > 0, beginFileUpload, endFileUpload }}>
			{children}
		</FileUploadActivityContext.Provider>
	);
}

export function useFileUploadActivity(): FileUploadActivityContextValue {
	const ctx = useContext(FileUploadActivityContext);
	if (!ctx) throw new Error('useFileUploadActivity must be used within a FileUploadActivityProvider');
	return ctx;
}
